import React, { useEffect, useState } from "react";

import { useGetProductTemplateInfos } from "api/api-hooks/product-templates-hooks";
import { ProductTemplateTaskSelectionOptions } from "api/model/product-template/ProductTemplateTaskSelectionOptions";
import DatasetTitle from "components/basic/display-titles/DatasetTitle";
import { DatasetDropdown } from "components/basic/dropdowns/dataset/DatasetDropdown";
import { ProductTemplateDropdown } from "components/basic/dropdowns/product-template/ProductTemplateDropdown";
import ShortText from "components/basic/short-text/ShortText";
import { easasAlert } from "components/fragments/easas-global-fragment/EasasGlobalAlerts";
import { InputChangeEvent } from "components/pages/scheduled-task-airflow/components/InputFields";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { Column } from "primereact/column";
import { confirmPopup } from "primereact/confirmpopup";
import { DataTable } from "primereact/datatable";
import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { T } from "services/lang/LanguageService";

interface ITaskConfigProductTemplatesTableProps {
  header: string;
  productTemplates: Record<number, ProductTemplateTaskSelectionOptions>;
  onChange: (e: InputChangeEvent<Record<number, ProductTemplateTaskSelectionOptions>>) => void;
  bodyClass?: string;
}

interface IRowModelValues extends ProductTemplateTaskSelectionOptions {
  prodTemplateId: number | undefined;
}

interface IProductTemplateRowModel {
  key: number;
  newKey: number | null;
  value: IRowModelValues;
  newValue: IRowModelValues;
  editMode: boolean;
}

const defaultRowValues: IRowModelValues = {
  prodTemplateId: undefined,
  entryDataset: null,
  latestEntryCount: 10,
  useDataset: false,
};

export default function TaskConfigProductTemplatesTable(props: ITaskConfigProductTemplatesTableProps) {
  const [dataArr, setDataArr] = useState<IProductTemplateRowModel[]>(mapToList(props.productTemplates));
  const [prodTemplateTitleMap, setProdTemplateTitleMap] = useState<Record<number, string>>({});

  const prodQuery = useGetProductTemplateInfos();
  // const datasetQuery = useGetDatasetInfos();

  useEffect(() => {
    if (prodQuery.data) {
      const titleMap: Record<number, string> = {};
      prodQuery.data.forEach(it => {
        titleMap[it.id!!] = it.displayName;
      });
      setProdTemplateTitleMap(titleMap);
    }
  }, [prodQuery.data, dataArr]);

  const propagateChangesStateUp = (arr: IProductTemplateRowModel[]) => {
    const tmp: Record<number, ProductTemplateTaskSelectionOptions> = {};

    arr.forEach(it => {
      if (it.value) {
        // Template id must be defined
        tmp[it.key] = {
          latestEntryCount: it.value.latestEntryCount,
          entryDataset: it.value.entryDataset,
          useDataset: it.value.useDataset,
          filterQuery: it.value.filterQuery,
        };
      }
    });

    props.onChange({ property: "productTemplates", value: tmp } as InputChangeEvent<
      Record<number, ProductTemplateTaskSelectionOptions>
    >);
  };

  const confirmWrapper = (event: any, confirmHandler: any) => {
    confirmPopup({
      target: event.currentTarget,
      message: T("confirm-dialog.are-you-really-want-to-delete-this-product-template-section"),
      icon: "pi pi-exclamation-triangle",
      accept: confirmHandler,
    });
  };

  const showWarn = (warn: string) => {
    easasAlert({ summary: T("word.invalid"), severity: "warn", detail: warn, life: 3000 });
  };

  const handleEdit = (row: IProductTemplateRowModel) => {
    const idx = dataArr.indexOf(row);

    dataArr[idx].editMode = true;
    dataArr[idx].newValue = { ...dataArr[idx].value };
    dataArr[idx].newKey = dataArr[idx].key;
    setDataArr([...dataArr]);
  };

  const handleEditCancel = (row: IProductTemplateRowModel) => {
    const idx = dataArr.indexOf(row);

    // Check if item is selected, if not remove row
    if (row.newValue == null || row.value == null) {
      setDataArr([...dataArr.filter(item => item.key !== row.key)]);
      return;
    }

    dataArr[idx].editMode = false;
    dataArr[idx].newValue = dataArr[idx].value;
    dataArr[idx].newKey = dataArr[idx].key;
    setDataArr([...dataArr]);
  };

  const handleEditConfirm = (row: IProductTemplateRowModel) => {
    const idx = dataArr.indexOf(row);

    if (dataArr[idx].newKey == null) {
      showWarn(T("alert.product-template-must-be-selected"));
      return;
    }

    if (dataArr[idx].newValue.useDataset && dataArr[idx].newValue.entryDataset == null) {
      showWarn(T("alert.dataset-must-be-selected"));
      return;
    }

    dataArr[idx].editMode = false;
    dataArr[idx].value = dataArr[idx].newValue;
    dataArr[idx].key = dataArr[idx].newKey!!;

    const updated = [...dataArr];
    setDataArr(updated);
    propagateChangesStateUp(updated);
  };

  const handleAdd = () => {
    const row = {
      newKey: null,
      newValue: { ...defaultRowValues },
      editMode: true,
    } as IProductTemplateRowModel;
    const updated = [...dataArr, row];
    setDataArr(updated);
  };

  const handleDelete = (row: IProductTemplateRowModel) => {
    const filtered = [...dataArr.filter(item => item.key !== row.key)];
    setDataArr(filtered);
    propagateChangesStateUp(filtered);
  };

  const handleProdTemplateChange = (e: number, row: IProductTemplateRowModel) => {
    const idx = dataArr.indexOf(row);
    dataArr[idx].newValue.prodTemplateId = e;
    dataArr[idx].newKey = e;
    setDataArr([...dataArr]);
  };

  const headerTemplate = (
    <div className="flex justify-content-between">
      <h4>{props.header}</h4>
      <Button icon="pi pi-plus" className="p-button-success" onClick={() => handleAdd()} />
    </div>
  );

  const handleChange = (property: keyof IRowModelValues, value: any, row: IProductTemplateRowModel) => {
    const idx = dataArr.indexOf(row);
    dataArr[idx].newValue = { ...dataArr[idx].newValue, [property]: value };
    setDataArr([...dataArr]);
  };

  const productTemplateBody = (rowData: IProductTemplateRowModel) => {
    return (
      <>
        {!rowData.editMode && prodTemplateTitleMap[rowData.value.prodTemplateId!!]}

        {rowData.editMode && (
          <ProductTemplateDropdown
            value={rowData.newValue.prodTemplateId}
            onChange={e => {
              handleProdTemplateChange(e.value, rowData);
            }}
          />
        )}
      </>
    );
  };

  const useDatasetTemplate = (rowData: IProductTemplateRowModel) => {
    return (
      <div className="flex justify-content-center">
        {!rowData.editMode && rowData.value.useDataset && <i className="pi pi-check-circle text-green-600" />}

        {rowData.editMode && (
          <Checkbox
            checked={rowData.newValue.useDataset}
            onChange={e => handleChange("useDataset", e.checked, rowData)}
          />
        )}
      </div>
    );
  };

  const datasetTemplate = (rowData: IProductTemplateRowModel) => {
    return (
      <>
        {!rowData.editMode && rowData.value.useDataset && (
          <DatasetTitle identifier={rowData.value.entryDataset} />
        )}

        {rowData.editMode && rowData.newValue.useDataset && (
          <DatasetDropdown
            value={rowData.newValue.entryDataset}
            onChange={e => handleChange("entryDataset", e, rowData)}
          />
        )}
      </>
    );
  };

  const entryCountTemplate = (rowData: IProductTemplateRowModel) => {
    return (
      <>
        {!rowData.editMode && rowData.value.latestEntryCount}

        {rowData.editMode && (
          <InputNumber
            placeholder={T("component.entry-count")}
            value={rowData.newValue.latestEntryCount}
            onChange={e => {
              handleChange("latestEntryCount", e.value, rowData);
            }}
          />
        )}
      </>
    );
  };

  const filterQueryTemplate = (rowData: IProductTemplateRowModel) => {
    return (
      <>
        {!rowData.editMode && <ShortText text={rowData.value.filterQuery} />}

        {rowData.editMode && (
          <InputText
            placeholder={T("word.filter")}
            value={rowData.newValue.filterQuery}
            onChange={e => {
              handleChange("filterQuery", e.target.value, rowData);
            }}
          />
        )}
      </>
    );
  };

  const actionBodyTemplate = (row: IProductTemplateRowModel) => {
    return (
      <>
        {!row.editMode && (
          <Button
            icon="pi pi-pencil"
            className="p-button-rounded p-button-text"
            onClick={() => handleEdit(row)}
          />
        )}

        {row.editMode && (
          <Button
            icon="pi pi-check"
            className="p-button-rounded p-button-text"
            onClick={() => handleEditConfirm(row)}
          />
        )}

        {row.editMode && (
          <Button
            icon="pi pi-times"
            className="p-button-plain p-button-rounded p-button-text"
            onClick={() => handleEditCancel(row)}
          />
        )}

        <Button
          icon="pi pi-trash"
          className="const-del-btn  p-button-rounded p-button-text p-button-danger"
          onClick={e => confirmWrapper(e, () => handleDelete(row))}
        />
      </>
    );
  };

  return (
    <DataTable value={dataArr} header={headerTemplate} width="100%" className={props.bodyClass} size="small">
      <Column header={T("load-conf.product-template")} body={productTemplateBody} style={{ width: "20%" }} />
      <Column header={T("component.entry-count")} body={entryCountTemplate} style={{ width: "10%" }} />
      <Column header={T("word.filter")} body={filterQueryTemplate} style={{ width: "20%" }} />
      <Column header={T("action.use-dataset")} body={useDatasetTemplate} style={{ width: "10%" }} />
      <Column header={T("component.entries-dataset")} body={datasetTemplate} />
      <Column body={actionBodyTemplate} bodyStyle={{ textAlign: "right", width: "12rem" }} />
    </DataTable>
  );
}

function mapToList(map: Record<number, ProductTemplateTaskSelectionOptions>): IProductTemplateRowModel[] {
  return Object.keys(map).map(k => {
    const id = parseInt(k, 10);
    const rowValues = { ...map[id], prodTemplateId: id } as IRowModelValues;
    return { key: id, value: rowValues, editMode: false } as IProductTemplateRowModel;
  });
}
